
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useProducts } from '../../hooks/useProducts';
import SearchBar from './SearchBar';

const Header: React.FC = () => {
    const location = useLocation();
    const { user, signOutUser } = useAuth();
    const { totalItems } = useCart();
    const { brandingSettings } = useProducts();
    
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Mostrar la barra de búsqueda solo en las páginas de detalle de producto (ej: /categoria/slug-producto)
    const isProductPage = useMatch("/:categorySlug/:productSlug");
    const showSearch = !!isProductPage;

    useEffect(() => {
        setIsMobileSearchOpen(false);
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const linkClasses = (path: string) =>
        `px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 ${
            (location.pathname.startsWith(path) && path !== '/') || location.pathname === path
            ? 'bg-brand-primary text-white'
            : 'text-gray-700 hover:bg-brand-primary/10 hover:text-brand-primary'
        }`;

    const mobileLinkClasses = (path: string) =>
        `block px-4 py-3 rounded-md text-base font-medium transition-colors duration-300 ${
            (location.pathname.startsWith(path) && path !== '/') || location.pathname === path
            ? 'bg-brand-primary text-white'
            : 'text-gray-700 hover:bg-brand-primary/10 hover:text-brand-primary'
        }`;

    const cartLinkClasses = `flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 border-2 ${
        location.pathname === '/carrito'
        ? 'border-brand-primary bg-brand-primary text-white'
        : 'border-brand-secondary text-brand-primary hover:bg-brand-primary/5'
    }`;

    return (
        <header className="bg-white shadow-md sticky top-0 z-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative flex items-center justify-between h-16 gap-4">
                    {/* Left: Logo */}
                    <div className="flex-shrink-0">
                        <Link to="/" className="flex items-center space-x-2">
                            {brandingSettings.logoUrl ? (
                                <img src={brandingSettings.logoUrl} alt={brandingSettings.platformName} className="h-8 w-8 object-contain" />
                            ) : (
                                <i className={`${brandingSettings.logoIcon} text-3xl text-brand-primary`}></i>
                            )}
                            <span className="text-xl sm:text-2xl font-bold text-gray-800">
                                {brandingSettings.platformNameAccent && brandingSettings.platformName.includes(brandingSettings.platformNameAccent)
                                    ? <>{brandingSettings.platformName.split(brandingSettings.platformNameAccent)[0]}<span className="text-brand-primary">{brandingSettings.platformNameAccent}</span>{brandingSettings.platformName.split(brandingSettings.platformNameAccent).slice(1).join(brandingSettings.platformNameAccent)}</>
                                    : brandingSettings.platformName
                                }
                            </span>
                        </Link>
                    </div>

                    {/* Center: Search Bar (Desktop) */}
                    {showSearch && (
                        <div className="flex-1 hidden md:flex justify-center items-center px-8 lg:px-16">
                            <div className="w-full max-w-lg relative top-[6px]">
                                <SearchBar />
                            </div>
                        </div>
                    )}

                    {/* Right: Nav & Actions */}
                    <div className="flex-shrink-0 flex items-center">
                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center space-x-4">
                            <Link to="/" className={linkClasses('/')}>Inicio</Link>
                            
                            {!user && (
                                <Link to="/track" className={linkClasses('/track')}>Rastrear Pedido</Link>
                            )}
                            
                            {user ? (
                                <>
                                    <Link to="/mis-pedidos" className={linkClasses('/mis-pedidos')}>
                                        Mis Pedidos
                                    </Link>
                                    
                                    {user.email === 'impresion007@gmail.com' && (
                                        <Link to="/admin" className={linkClasses('/admin')}>
                                            <i className="fas fa-user-shield mr-2"></i>Admin
                                        </Link>
                                    )}
                                    
                                    <span className="px-2 py-2 text-sm font-medium text-gray-500 truncate max-w-xs" title={user.email || ''}>
                                        | {user.displayName?.split(' ')[0] || 'Cuenta'}
                                    </span>
                                    
                                    <button onClick={signOutUser} className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-red-100 hover:text-red-700" aria-label="Cerrar sesión">
                                        Salir
                                    </button>
                                </>
                            ) : (
                                 <Link to="/checkout/auth" className={linkClasses('/checkout/auth')}>
                                    Iniciar Sesión
                                </Link>
                            )}

                            {totalItems > 0 && (
                                 <Link to="/carrito" className={cartLinkClasses}>
                                    <i className="fas fa-shopping-cart mr-2 opacity-70"></i>
                                    Carrito ({totalItems})
                                 </Link>
                            )}
                        </nav>
                        
                        {/* Mobile Nav Actions */}
                         <div className="md:hidden flex items-center space-x-4">
                            {showSearch && (
                                <button onClick={() => setIsMobileSearchOpen(true)} className="text-gray-600 hover:text-gray-800" aria-label="Buscar productos">
                                    <i className="fas fa-search text-2xl"></i>
                                </button>
                            )}
                            {totalItems > 0 && (
                                <Link to="/carrito" className="text-brand-primary relative">
                                    <i className="fas fa-shopping-cart text-2xl"></i>
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                        {totalItems}
                                    </span>
                                </Link>
                            )}
                            <button onClick={() => setIsMobileMenuOpen(true)} className="text-gray-600 hover:text-gray-800" aria-label="Abrir menú de navegación">
                                <i className="fas fa-bars text-2xl"></i>
                            </button>
                        </div>
                    </div>
                    
                    {/* Mobile Search Overlay */}
                    {isMobileSearchOpen && showSearch && (
                        <div className="absolute top-0 left-0 w-full h-16 bg-white z-20 flex items-center p-2 animate-fadeIn">
                            <div className="w-full flex">
                                <SearchBar />
                                <button onClick={() => setIsMobileSearchOpen(false)} className="ml-2 text-2xl text-gray-600 px-2" aria-label="Cerrar búsqueda">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Menu Panel */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}></div>
                    <div className="fixed top-0 right-0 h-full w-64 bg-white p-5 z-50 animate-slideUp">
                        <button onClick={() => setIsMobileMenuOpen(false)} className="absolute top-4 right-4 text-gray-500" aria-label="Cerrar menú de navegación">
                            <i className="fas fa-times text-2xl"></i>
                        </button>
                        <nav className="mt-8 flex flex-col space-y-4">
                            <Link to="/" className={mobileLinkClasses('/')}>Inicio</Link>
                            
                            {!user && (
                                <Link to="/track" className={mobileLinkClasses('/track')}>Rastrear Pedido</Link>
                            )}

                             {user ? (
                                <>
                                    <Link to="/mis-pedidos" className={mobileLinkClasses('/mis-pedidos')}>
                                        Mis Pedidos
                                    </Link>
                                    
                                    {user.email === 'impresion007@gmail.com' && (
                                        <Link to="/admin" className={mobileLinkClasses('/admin')}>
                                            <i className="fas fa-user-shield mr-2"></i>Admin
                                        </Link>
                                    )}
                                    
                                    <button onClick={signOutUser} className="w-full text-left px-4 py-3 rounded-md text-base font-medium text-red-700 bg-red-50 hover:bg-red-100" aria-label="Cerrar sesión">
                                        Salir
                                    </button>
                                </>
                            ) : (
                                 <Link to="/checkout/auth" className={mobileLinkClasses('/checkout/auth')}>
                                    Iniciar Sesión
                                </Link>
                            )}
                        </nav>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
